<template>
  <div>
    <v-layout row wrap>
      <v-flex xs12>
        <div v-if="$apollo.loading">
          <span class="page-title"> Loading... </span>
        </div>
        <div v-else>
          <v-layout class="update-container">
            <v-flex xs12 md6>
              <form v-scroll-to-error @submit.prevent="submitForm">
                <div class="screen-title">
                  <span>Update Accreditation</span>
                </div>
                <accreditation-form
                  :event="event"
                  :initial-values="form"
                  :public-form="false"
                  :vs="$v"
                  :styles="customStyles.attendeeForm && customStyles.attendeeForm.form"
                  :fields="schema.attendeeFields"
                  @values="form = $event"
                />
                <div
                  v-if="event.flags && event.flags.timeRestrictedAttendeeEntrance"
                  style="display: flex; gap: 8px"
                >
                  <datetime-field
                    v-model="form.entryFrom"
                    label="Entry from*"
                    style="width: 100%"
                    :max="event.to.toString()"
                  />
                  <datetime-field
                    v-model="form.entryTo"
                    style="width: 100%"
                    :min="event.from.toString()"
                    label="Entry to*"
                  />
                </div>
                <div class="buttons-container">
                  <main-button flat @click.prevent="goBack"> Cancel </main-button>
                  <span class="lower-buttons">
                    <main-button depressed @click="$emit('submit')"> Update </main-button>
                    <main-button flat color="error" @click.prevent="isVisibleDeleteDialog = true">
                      Delete
                    </main-button>
                  </span>
                </div>
              </form>
            </v-flex>
            <v-flex v-if="status === 'ALLOWED'" xs12 md6 class="pl-32 print-container">
              <div>
                <div class="page-title">
                  <span>Prints</span>
                </div>
                <print-list :prints="prints" />
                <v-tooltip v-if="!designSet" top color="#979797">
                  <main-button slot="activator" text="Print and return" disabled />
                  <span>
                    Before printing the accreditations, you need to create a template for them in
                    Design Tool.</span
                  >
                </v-tooltip>
                <div v-else style="display: inline">
                  <main-button slot="activator" depressed text="Print and return" @click="print" />
                </div>
              </div>
            </v-flex>
          </v-layout>
        </div>
      </v-flex>
    </v-layout>
    <delete-dialog
      :content="`Are you sure you want to delete accreditation? This operation cannot be undone.`"
      :on-delete="() => deleteAttendee(accreditationId, eventId)"
      :show.sync="isVisibleDeleteDialog"
      title="Delete accreditation"
    />
  </div>
</template>

<script>
import {
  UPDATE_ATTENDEE,
  DELETE_ATTENDEE,
  GET_ATTENDEE,
} from './../../graphql/accreditation.graphql'
import DeleteDialog from './../shared/dialogs/delete-dialog.vue'
import PrintList from './print-list'
import AccreditationForm from './accreditation-form.vue'
import { frontendURL, submitGuard, createAttendeeSchema } from './../../utils'
import MainButton from './../shared/main-button'

import { required, email, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'accreditation-update-container',
  components: {
    AccreditationForm,
    DeleteDialog,
    PrintList,
    MainButton,
  },
  props: {
    accreditationId: String | Number,
    eventId: String,
    organizationSlug: String,
  },
  data: () => ({
    permission: null,
    status: null,
    form: null,
    event: null,
    prints: null,
    isVisibleDeleteDialog: false,
    schema: {},
    customStyles: {},
    update: undefined,
  }),
  validations() {
    return {
      form: {
        category: { required: requiredIf(() => this.event.extraModules.categoriesAndZones) },
        zones: { required: requiredIf(() => this.event.extraModules.categoriesAndZones) },
        ...this.schema.validationSchema,
      },
    }
  },
  computed: {
    designSet() {
      return !!this.event.builder
    },
  },
  methods: {
    async print() {
      await submitGuard(this, async () => {
        await this.updateAttendee(this.form)
        const url = `${frontendURL}/organizations/${this.organizationSlug}/events/${this.event.id}/accreditation/${this.accreditationId}/edit/print`
        window.open(url, '_blank', 'noopener')
        this.goTo(this.routes.organizer.accreditations)
      })
    },
    async submitForm() {
      await submitGuard(this, async () => {
        await this.updateAttendee(this.form)
        this.goBack()
      })
    },
    async updateAttendee(values) {
      const { category, zones, avatar, ...rest } = values
      const attendee = {
        fields: rest,
        categoryId: category || null,
        zones,
        avatar,
      }

      return this.$apollo.mutate({
        mutation: UPDATE_ATTENDEE,
        variables: {
          eventId: this.eventId,
          attendeeId: this.accreditationId,
          attendee,
        },
      })
    },
    async deleteAttendee(attendeeId, eventId) {
      await this.$apollo.mutate({
        mutation: DELETE_ATTENDEE,
        variables: { eventId, attendeeId },
        refetchQueries: ['getEventAccreditations'],
      })
      this.goBack()
    },
  },
  apollo: {
    getAttendee: {
      query: GET_ATTENDEE,
      variables() {
        return {
          eventId: this.eventId,
          attendeeId: this.accreditationId,
        }
      },
      result({ data, loading, errors }) {
        if (errors) {
          return this.goBack()
        }
        if (!loading) {
          const { getAttendee, getEventCategories } = data
          if (!getAttendee) {
            this.goBack()
          }
          const { status, category, prints, fields, zones, avatar, ...rest } = getAttendee
          this.form = {
            category: (category && category.id) || null,
            zones,
            avatar,
            ...fields,
          }
          this.status = status
          this.prints = prints
          this.event = getEventCategories
          this.customStyles =
            (this.event.eventUtils &&
              this.event.eventUtils.inviteFormStyles &&
              this.event.eventUtils.inviteFormStyles) ||
            {}
          const { attendeeFields } = this.event
          this.schema = createAttendeeSchema(attendeeFields, fields)
        }
      },
    },
  },
}
</script>

<style scoped>
@media (max-width: 800px) {
  .update-container {
    display: flex;
    flex-direction: column;
    margin-top: 1.5rem;
  }
  .print-container {
    margin-top: 12px;
    padding-left: 0px !important;
  }
}
</style>
